import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  ALL_DENIED_STATE,
  ALL_GRANTED_STATE,
  DEFAULT_DENIED_STATE,
  loadStoredConsent,
  updateConsent,
} from "@/lib/consent";

const CookiePrefsContext = createContext(null);

export function CookiePrefsProvider({ children }) {
  const [state, setState] = useState(DEFAULT_DENIED_STATE);
  const [hasConsent, setHasConsent] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);

  // Load saved consent (or show banner)
  useEffect(() => {
    const stored = loadStoredConsent();
    if (stored && stored.state) {
      setState(stored.state);
      setHasConsent(true);
      setBannerVisible(false);
    } else {
      setHasConsent(false);
      setBannerVisible(true);
    }
  }, []);

  const acceptAll = () => {
    setState(ALL_GRANTED_STATE);
    updateConsent(ALL_GRANTED_STATE, "all");
    setHasConsent(true);
    setBannerVisible(false);
    setManagerOpen(false);
  };

  const rejectAll = () => {
    setState(ALL_DENIED_STATE);
    updateConsent(ALL_DENIED_STATE, "none");
    setHasConsent(true);
    setBannerVisible(false);
    setManagerOpen(false);
  };

  const saveCustom = (custom) => {
    // functionality_storage & security_storage always granted
    const next = {
      ...custom,
      functionality_storage: "granted",
      security_storage: "granted",
    };
    setState(next);
    updateConsent(next, "custom");
    setHasConsent(true);
    setBannerVisible(false);
    setManagerOpen(false);
  };

  const openManager = () => setManagerOpen(true);
  const closeManager = () => setManagerOpen(false);
  const openBanner = () => setBannerVisible(true);

  const value = useMemo(
    () => ({
      state,
      hasConsent,
      bannerVisible,
      managerOpen,
      acceptAll,
      rejectAll,
      saveCustom,
      openManager,
      closeManager,
      openBanner,
    }),
    [state, hasConsent, bannerVisible, managerOpen]
  );

  return (
    <CookiePrefsContext.Provider value={value}>
      {children}
    </CookiePrefsContext.Provider>
  );
}

export function useCookiePrefs() {
  const ctx = useContext(CookiePrefsContext);
  if (!ctx) throw new Error("useCookiePrefs must be used within CookiePrefsProvider");
  return ctx;
}
